import React, { useEffect } from "react"
import { makeStyles } from "@material-ui/core/styles"
import { Grid, Typography } from "@material-ui/core"
import styled, { keyframes } from "styled-components"

import Layout from "../components/Layout"
import Seo from "../components/SEO"
import { responsiveFontSize } from "../components/Theme"
import starBackground from "../images/star_background.jpeg"

// 縁取り文字。文字色(color)と影(shadow)の組み合わせだけが異なるため1つに集約
const OutlinedTxt = styled.span`
  display: inline-block;
  color: ${props => props.color};
  letter-spacing: 4px; /* 文字間 */
  text-shadow: ${props => props.shadow};
`

const GREY_SHADOW = "1px 1px 0px grey"
const ORANGE_SHADOW = "4px 4px 0px darkorange"
const BADGE_SHADOW =
  "2px 2px 0px white, -2px -2px 0px white, 4px 4px 0px darkorange"

// 白背景上でもWCAG AAのコントラスト比(4.5:1)を満たす赤
const ACCENT_RED = "#c00000"

// 白背景上でもWCAG AAを満たす金色(4.93:1)とグレー(4.6:1)
const GOLD_TXT = "#8a6d00"
const GREY_TXT = "#757575"

// 表・注記など料金表まわりの本文書体(サイト既定の明朝系と区別する)
const MARU_GOTHIC =
  '"ヒラギノ丸ゴ ProN W6", "Hiragino Maru Gothic ProN", "Zen Maru Gothic", Meiryo, sans-serif'

// PC/SPの切り替え幅はLayout/style.cssの.pc-block/.sp-blockと同じ680pxに揃える
const SP_MAX = "@media (max-width: 680px)"

// ---------------------------------------------------------------------------
// 料金データ(税込)。n=通常価格 / d=前日割価格。src/images/pc_menu1〜4.pngの
// 旧料金表画像を文字起こししたもの。価格改定時はここだけを直せばよい
// ---------------------------------------------------------------------------
const cell = (n, d) => ({ n, d })

const COLS6 = ["~2cm", "~4cm", "~6cm", "~8cm", "~10cm", "Max"]
const COLS4 = ["S", "M", "L", "LL"]

const HAIR_ROWS = [
  {
    label: "●ベーシック",
    color: "blue",
    cells: [
      cell("4,235", "3,388"),
      cell("4,840", "3,872"),
      cell("5,445", "4,356"),
      cell("6,050", "4,840"),
      cell("6,655", "5,324"),
      cell("7,260", "5,808"),
    ],
  },
  {
    label: "◎プレミアム",
    color: "darkorchid",
    cells: [
      cell("5,445", "4,356"),
      cell("6,050", "4,840"),
      cell("6,655", "5,324"),
      cell("7,260", "5,808"),
      cell("7,865", "6,292"),
      cell("8,470", "6,776"),
    ],
  },
]

const BLEACH_RETOUCH = [
  cell("6,655", "5,324"),
  cell("7,260", "5,808"),
  cell("7,865", "6,292"),
  cell("8,470", "6,776"),
  cell("9,075", "7,260"),
  cell("9,680", "7,744"),
]

const BLEACH_FULL = [
  cell("9,680", "7,744"),
  cell("10,890", "8,712"),
  cell("12,100", "9,680"),
  cell("13,310", "10,648"),
]

const MANICURE_CELLS = [
  cell("4,235", "3,388"),
  cell("4,840", "3,872"),
  cell("5,445", "4,356"),
  cell("6,050", "4,840"),
  cell("6,655", "5,324"),
  cell("7,260", "5,808"),
]

const KURO_CELLS = [
  cell("4,840", "3,872"),
  cell("6,050", "4,840"),
  cell("7,260", "5,808"),
  cell("8,470", "6,776"),
]

const DESIGN_ROWS = [
  {
    pre: "☆",
    name: "インナー",
    color: "magenta",
    cells: [
      cell("4,840", "3,872"),
      cell("5,445", "4,356"),
      cell("6,050", "4,840"),
      cell("6,655", "5,324"),
    ],
  },
  {
    pre: "★",
    name: "ツートーン",
    color: "#7CFC00",
    cells: [
      cell("4,840", "3,872"),
      cell("5,445", "4,356"),
      cell("6,050", "4,840"),
      cell("6,655", "5,324"),
    ],
  },
  {
    pre: "☆",
    name: "グラデーション",
    color: "orange",
    cells: [
      cell("6,050", "4,840"),
      cell("7,260", "5,808"),
      cell("8,470", "6,776"),
      cell("9,680", "7,744"),
    ],
  },
  {
    pre: "★",
    name: "メッシュ",
    color: "yellow",
    cells: [
      cell("6,050", "4,840"),
      cell("7,260", "5,808"),
      cell("8,470", "6,776"),
      cell("9,680", "7,744"),
    ],
  },
  {
    pre: "☆ブリーチ",
    name: "インナー",
    color: "magenta",
    cells: [
      cell("7,260", "5,808"),
      cell("7,865", "6,292"),
      cell("8,470", "6,776"),
      cell("9,075", "7,260"),
    ],
  },
  {
    pre: "★ブリーチ",
    name: "ツートーン",
    color: "#7CFC00",
    cells: [
      cell("7,260", "5,808"),
      cell("7,865", "6,292"),
      cell("8,470", "6,776"),
      cell("9,075", "7,260"),
    ],
  },
  {
    pre: "☆ブリーチ",
    name: "グラデーション",
    color: "orange",
    cells: [
      cell("8,470", "6,776"),
      cell("9,680", "7,744"),
      cell("10,890", "8,712"),
      cell("12,100", "9,680"),
    ],
  },
  {
    pre: "★ブリーチ",
    name: "メッシュ",
    color: "yellow",
    cells: [
      cell("8,470", "6,776"),
      cell("9,680", "7,744"),
      cell("10,890", "8,712"),
      cell("12,100", "9,680"),
    ],
  },
]

const EXTRA_ROWS = [
  {
    parts: [
      { t: "☆追い", c: "white" },
      { t: "カラー", c: "magenta" },
    ],
    cells: ["1,210", "1,815", "2,420", "3,025"],
  },
  {
    parts: [
      { t: "★", c: "white" },
      { t: "アッシュ", c: "orange" },
      { t: "シャンプー", c: "cyan" },
      { t: "仕上", c: "white" },
    ],
    cells: ["1,210", "1,815", "2,420", "3,025"],
  },
]

// 黒背景セクションに散らすきらめき。SSRとハイドレーションで一致させるため
// 乱数ではなく固定配置(デザインのランダム生成結果を焼き込んだもの)
const SPARKLES = [
  { x: 4, y: 8, size: 18, color: "yellow", char: "★", dur: 2.8, delay: 0.2 },
  { x: 14, y: 62, size: 12, color: "white", char: "☆", dur: 3.6, delay: 1.4 },
  { x: 22, y: 24, size: 22, color: "magenta", char: "✦", dur: 2.4, delay: 0.8 },
  { x: 30, y: 78, size: 14, color: "cyan", char: "✧", dur: 4.2, delay: 2.1 },
  { x: 38, y: 12, size: 16, color: "#7CFC00", char: "★", dur: 3.1, delay: 1.0 },
  { x: 46, y: 55, size: 24, color: "orange", char: "☆", dur: 2.6, delay: 0.4 },
  { x: 54, y: 30, size: 12, color: "yellow", char: "✦", dur: 3.9, delay: 1.8 },
  { x: 61, y: 84, size: 18, color: "white", char: "✧", dur: 2.9, delay: 0.6 },
  { x: 68, y: 18, size: 14, color: "magenta", char: "★", dur: 3.4, delay: 2.6 },
  { x: 75, y: 66, size: 20, color: "cyan", char: "☆", dur: 2.3, delay: 1.2 },
  { x: 82, y: 40, size: 12, color: "#7CFC00", char: "✦", dur: 4.4, delay: 0.1 },
  { x: 88, y: 90, size: 16, color: "orange", char: "✧", dur: 3.2, delay: 2.3 },
  { x: 93, y: 10, size: 22, color: "yellow", char: "★", dur: 2.7, delay: 1.6 },
  { x: 97, y: 50, size: 13, color: "white", char: "☆", dur: 3.7, delay: 0.9 },
]

// ---------------------------------------------------------------------------
// アニメーション
// ---------------------------------------------------------------------------
const floatY = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`

const twinkle = keyframes`
  0%, 100% { opacity: 0.15; transform: scale(0.6); }
  50% { opacity: 1; transform: scale(1.15); }
`

const FloatingTxt = styled(OutlinedTxt)`
  animation: ${floatY} 3.2s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const Sparkle = styled.span`
  position: absolute;
  left: ${p => p.$x}%;
  top: ${p => p.$y}%;
  font-size: ${p => p.$size}px;
  color: ${p => p.$color};
  opacity: 0;
  pointer-events: none;
  animation: ${twinkle} ${p => p.$dur}s ease-in-out ${p => p.$delay}s infinite;
  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`

// スクロールに応じて[data-reveal]をフェードインさせる。初期状態は「見える」
// ままJSで隠してから表示するため、JS無効環境やSSR時も内容は閲覧できる
const useScrollReveal = () => {
  useEffect(() => {
    if (
      typeof IntersectionObserver === "undefined" ||
      (window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches)
    ) {
      return undefined
    }
    const els = Array.from(document.querySelectorAll("[data-reveal]"))
    els.forEach(el => {
      el.style.opacity = "0"
      el.style.transform = "translateY(24px)"
      el.style.transition = "opacity 0.8s ease, transform 0.8s ease"
    })
    const io = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.style.opacity = "1"
            e.target.style.transform = "none"
            io.unobserve(e.target)
          }
        })
      },
      { threshold: 0.12 }
    )
    els.forEach(el => io.observe(el))
    return () => io.disconnect()
  }, [])
}

// ---------------------------------------------------------------------------
// 料金表のレイアウト部品。CSS Gridで組み、SP(680px以下)ではラベル列を畳んで
// ラベルを表の上に全幅で表示する
// ---------------------------------------------------------------------------
const HairTables = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem 1.2rem;
  font-family: ${MARU_GOTHIC};
`

const DarkTables = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 1.2rem;
  color: white;
  font-family: ${MARU_GOTHIC};
`

const PriceTable = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$labelCol} repeat(${p => p.$cols}, 1fr);
  align-items: center;

  ${SP_MAX} {
    grid-template-columns: repeat(${p => p.$cols}, 1fr);
  }
`

const PlainTable = styled.div`
  background: white;
  color: black;
  display: grid;
  grid-template-columns: repeat(${p => p.$cols}, 1fr);
  ${p => (p.$narrow ? "max-width: 70%;" : "")}

  ${SP_MAX} {
    max-width: 100%;
  }
`

const Row = styled.div`
  display: contents;
`

const HeaderSpacer = styled.div`
  ${SP_MAX} {
    display: none;
  }
`

const HeadCell = styled.div`
  text-align: center;
  font-size: clamp(14px, 1.8vw, 22px);
  font-weight: 600;
  border-left: 1px solid ${p => p.$border};
  padding: 0.4rem 0.2rem;
`

const RowLabel = styled.div`
  text-align: ${p => (p.$align === "right" ? "right" : "center")};
  padding: ${p =>
    p.$align === "right" ? "1.2rem 1.6rem 1.2rem 0.6rem" : "1rem 0.6rem"};

  ${SP_MAX} {
    grid-column: 1 / -1;
    text-align: center;
    padding: 1.2rem 0 0.4rem 0;
  }
`

// 黒魔術のラベルはPCでは見出し行+価格行の2行分の高さに縦センター配置する
const KuroLabel = styled(RowLabel)`
  grid-row: span 2;
  align-self: center;

  ${SP_MAX} {
    grid-row: auto;
  }
`

const RowLabelTxt = styled.span`
  font-size: clamp(15px, 2vw, 24px);
  font-weight: 700;
  letter-spacing: 1px;
  text-decoration: underline;
  white-space: nowrap;
`

const CellBox = styled.div`
  text-align: center;
  border-left: 1px solid ${p => p.$border};
  padding: 0.8rem 0.2rem;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const NormalPrice = styled.div`
  font-size: clamp(14px, 1.8vw, 21px);
`

const DiscountPrice = styled.div`
  color: ${p => p.$color};
  font-size: clamp(14px, 1.9vw, 22px);
  font-weight: 700;
`

const OptionCell = styled.div`
  text-align: center;
  border-left: 1px solid white;
  padding: 1rem 0.2rem;
  align-self: stretch;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(14px, 1.8vw, 21px);
`

const FoilCell = styled.div`
  grid-column: span 4;
  border-left: 1px solid white;
  padding: 1rem 1.2rem;
  font-size: clamp(14px, 1.8vw, 21px);
`

const FashionHeading = styled.h3`
  margin: 0 0 1.6rem 0;
  font-size: clamp(18px, 2.6vw, 30px);
  font-weight: 700;
  letter-spacing: 2px;
  font-family: ${MARU_GOTHIC};
`

const BleachBlock = styled.div`
  background: linear-gradient(to bottom, #f6c623, #e8a800);
  margin: 1.2rem 0;
  padding: 1.2rem;
  display: grid;
  grid-template-columns: minmax(150px, 1.2fr) 6fr;
  gap: 0.8rem;
  align-items: center;

  ${SP_MAX} {
    grid-template-columns: 1fr;
  }
`

const BleachTagTxt = styled.span`
  background: yellow;
  color: black;
  font-weight: 700;
  font-size: clamp(14px, 1.9vw, 22px);
`

const ExtraNote = styled.p`
  color: white;
  font-size: clamp(13px, 1.7vw, 20px);
  font-weight: 600;
  margin: 1.2rem 0 0 0;
  text-align: center;
  font-family: ${MARU_GOTHIC};
`

// 通常価格+前日割価格の1セル。「赤字=前日割」の意味は色でしか示されないため
// スクリーンリーダー向けにvisually-hiddenで補足する。
// ラベル列を持つ表ではSP時にHeaderSpacerがdisplay:noneでa11yツリーから消え、
// 暗黙の列計算が1列ずれるため、colIndexで明示的な列番号を渡す
const PriceCell = ({ cell: c, border, discountColor, colIndex }) => (
  <CellBox role="cell" aria-colindex={colIndex} $border={border}>
    <NormalPrice>
      {c.n}
      <span className="visually-hidden">円(通常)</span>
    </NormalPrice>
    <DiscountPrice $color={discountColor}>
      {c.d}
      <span className="visually-hidden">円(前日割)</span>
    </DiscountPrice>
  </CellBox>
)

// 見出し行(左上の空きマス+サイズ列)。全テーブルで同じ構造の繰り返しを共通化。
// withSpacer時はラベル列が列1になるため、サイズ見出しに列2以降の番号を明示する
const HeadRow = ({ cols, border, withSpacer = true }) => (
  <Row role="row">
    {withSpacer && <HeaderSpacer role="cell" aria-colindex={1} />}
    {cols.map((c, i) => (
      <HeadCell
        key={c}
        role="columnheader"
        aria-colindex={withSpacer ? i + 2 : undefined}
        $border={border}
      >
        {c}
      </HeadCell>
    ))}
  </Row>
)

const titleInline = (theme, color) => ({
  fontFamily: "ヒラギノ丸ゴ ProN W6",
  fontWeight: 700,
  textDecoration: "underline",
  ...(color ? { color } : {}),
  ...responsiveFontSize(theme, { base: "2.2rem", sm: "2.5rem", md: "2.8rem" }),
})

const useStyles = makeStyles(theme => ({
  blackContainer: {
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#161616",
    paddingTop: "2rem",
    paddingBottom: "2rem",
  },
  titleContainer1: {
    background: "linear-gradient(to bottom, white, cyan, white)",
    padding: "1rem",
  },
  titleContainer2: {
    background: "#161616",
    padding: "1rem",
    marginBottom: "2rem",
  },
  hosokuTxt: responsiveFontSize(theme, {
    base: "1.2rem",
    sm: "1.6rem",
    md: "1.8rem",
  }),
  titleInlineLeft1: titleInline(theme),
  titleInlineLeft2: titleInline(theme, "red"),
  titleInlineLeft3: titleInline(theme, "white"),
  titleInlineRight1: titleInline(theme),
  titleInlineRight2: titleInline(theme, "cyan"),
  pointTitle: {
    fontFamily: "ヒラギノ丸ゴ ProN W6",
    color: "blue",
    marginBottom: "1.2rem",
    fontWeight: 700,
    ...responsiveFontSize(theme, {
      base: "1.8rem",
      sm: "2.2rem",
      md: "2.6rem",
    }),
  },
  pointDesc: {
    fontFamily: "ヒラギノ丸ゴ ProN W6",
    ...responsiveFontSize(theme, { base: "1.4rem", sm: "1.6rem", md: "2rem" }),
  },
  starSection: {
    backgroundImage: `url(${starBackground})`,
    backgroundRepeat: "repeat",
    backgroundSize: "100%",
    padding: "4rem 2rem",
  },
  pointBox: {
    backgroundColor: "white",
    padding: "1.6rem",
    maxWidth: "90rem",
    marginLeft: "auto",
    marginRight: "auto",
  },
}))

// 「左テキスト+バッジ」構成のセクションタイトル。ページ内で3回繰り返される構造を共通化
// MUI v4のspacingはnegative marginで横スクロールを生むため、gapで間隔を取る
const SectionTitle = ({ className, left, badge }) => (
  <Grid
    container
    justifyContent="center"
    alignItems="center"
    className={className}
    style={{ gap: "0.8rem 2rem" }}
  >
    <Grid item>
      <Typography variant="h1" component="h2" align="center">
        {left}
      </Typography>
    </Grid>
    <Grid item>
      <Typography variant="h1" component="h2">
        {badge}
      </Typography>
    </Grid>
  </Grid>
)

const MenuPage = () => {
  const classes = useStyles()
  useScrollReveal()

  return (
    <Layout>
      <Seo
        title="カラーメニュー・料金表"
        description="埼玉県東松山市の美容室BIGHEAD『カラーの魔術師』のヘアカラーメニュー・料金表(税込)。おしゃれ染め・白髪染め・デザインカラー・ブリーチ・エクストラOPTION。前日19時までのご予約で赤字価格の前日割も。"
        pathname="/menu/"
      />
      <h1 className="visually-hidden">
        ヘアカラーMENU・料金表|東松山市の美容室BIGHEAD カラーの魔術師
      </h1>
      <Typography variant="body2" align="right" className={classes.hosokuTxt}>
        <u>※全て税込価格です。</u>
      </Typography>
      <SectionTitle
        className={classes.titleContainer1}
        left={
          <>
            <OutlinedTxt
              className={classes.titleInlineLeft1}
              color="black"
              shadow={GREY_SHADOW}
            >
              ヘア
            </OutlinedTxt>
            <OutlinedTxt
              className={classes.titleInlineRight1}
              color="deeppink"
              shadow={GREY_SHADOW}
            >
              カラー
            </OutlinedTxt>
          </>
        }
        badge={
          <FloatingTxt color="#000000" shadow={BADGE_SHADOW}>
            MENU
          </FloatingTxt>
        }
      />
      <HairTables data-reveal>
        {/* 金色/グレーはデザイン由来だが、白背景でWCAG AA(4.5:1)を満たすよう
            #a07800→#8a6d00(ブリーチラベルと同色)、grey→#757575に暗めに調整 */}
        <FashionHeading>
          <span style={{ color: GOLD_TXT, textShadow: GREY_SHADOW }}>
            <u>おしゃれ染め</u>
          </span>
          <span style={{ color: GOLD_TXT, fontSize: "0.7em" }}>
            (ファッションカラー)
          </span>
          <span style={{ color: "#555" }}> / </span>
          <span style={{ color: GREY_TXT, textShadow: "1px 1px 0px silver" }}>
            <u>白髪染め</u>
          </span>
          <span style={{ color: GREY_TXT, fontSize: "0.7em" }}>
            (グレイカラー)
          </span>
        </FashionHeading>
        <PriceTable
          role="table"
          aria-label="おしゃれ染め・白髪染め料金表"
          aria-colcount={7}
          $labelCol="minmax(200px, 1.8fr)"
          $cols={6}
        >
          <HeadRow cols={COLS6} border="black" />
          {HAIR_ROWS.map(row => (
            <Row role="row" key={row.label}>
              <RowLabel role="rowheader" aria-colindex={1}>
                <RowLabelTxt style={{ color: row.color }}>
                  {row.label}
                </RowLabelTxt>
              </RowLabel>
              {row.cells.map((c, i) => (
                <PriceCell
                  key={i}
                  cell={c}
                  border="black"
                  discountColor={ACCENT_RED}
                  colIndex={i + 2}
                />
              ))}
            </Row>
          ))}
        </PriceTable>
        <BleachBlock>
          <div
            style={{
              textAlign: "center",
              fontSize: "clamp(18px, 2.6vw, 28px)",
              fontWeight: 700,
              letterSpacing: "2px",
            }}
          >
            <span
              style={{
                color: GOLD_TXT,
                textShadow: "1px 1px 0px #fff2b0",
                textDecoration: "underline",
              }}
            >
              ◆ブリーチ
            </span>
          </div>
          <div style={{ display: "grid", gap: "1rem" }}>
            <div>
              <div style={{ marginBottom: "0.4rem" }}>
                <BleachTagTxt>
                  <span style={{ color: "deeppink" }}>
                    <u>★リタッチ</u>
                  </span>
                  <u>の場合</u>
                </BleachTagTxt>
              </div>
              <PlainTable
                role="table"
                aria-label="ブリーチ(リタッチ)料金表"
                $cols={6}
              >
                <HeadRow cols={COLS6} border="black" withSpacer={false} />
                <Row role="row">
                  {BLEACH_RETOUCH.map((c, i) => (
                    <PriceCell
                      key={i}
                      cell={c}
                      border="black"
                      discountColor={ACCENT_RED}
                    />
                  ))}
                </Row>
              </PlainTable>
            </div>
            <div>
              <div style={{ marginBottom: "0.4rem" }}>
                <BleachTagTxt>
                  <span style={{ color: "deeppink" }}>
                    <u>★髪全体</u>
                  </span>
                  <u>の場合</u>
                </BleachTagTxt>
              </div>
              <PlainTable
                role="table"
                aria-label="ブリーチ(髪全体)料金表"
                $cols={4}
                $narrow
              >
                <HeadRow cols={COLS4} border="black" withSpacer={false} />
                <Row role="row">
                  {BLEACH_FULL.map((c, i) => (
                    <PriceCell
                      key={i}
                      cell={c}
                      border="black"
                      discountColor={ACCENT_RED}
                    />
                  ))}
                </Row>
              </PlainTable>
            </div>
          </div>
        </BleachBlock>
        <PriceTable
          role="table"
          aria-label="ヘアマニキュア料金表"
          aria-colcount={7}
          $labelCol="minmax(200px, 1.8fr)"
          $cols={6}
        >
          <HeadRow cols={COLS6} border="black" />
          <Row role="row">
            <RowLabel role="rowheader" aria-colindex={1}>
              <RowLabelTxt style={{ color: "rebeccapurple" }}>
                ◇ヘアマニキュア
              </RowLabelTxt>
            </RowLabel>
            {MANICURE_CELLS.map((c, i) => (
              <PriceCell
                key={i}
                cell={c}
                border="black"
                discountColor={ACCENT_RED}
                colIndex={i + 2}
              />
            ))}
          </Row>
        </PriceTable>
        <PriceTable
          role="table"
          aria-label="黒魔術(黒染め・黒髪戻し)料金表"
          aria-colcount={5}
          $labelCol="minmax(240px, 2.2fr)"
          $cols={4}
          style={{ marginTop: "1.6rem" }}
        >
          <Row role="row">
            <KuroLabel role="rowheader" aria-colindex={1} aria-rowspan={2}>
              <RowLabelTxt style={{ color: "black" }}>★黒魔術★</RowLabelTxt>
              <span
                style={{
                  fontSize: "clamp(13px, 1.7vw, 20px)",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                【黒染め(黒髪戻し)】
              </span>
            </KuroLabel>
            {COLS4.map((c, i) => (
              <HeadCell
                key={c}
                role="columnheader"
                aria-colindex={i + 2}
                $border="black"
              >
                {c}
              </HeadCell>
            ))}
          </Row>
          <Row role="row">
            {KURO_CELLS.map((c, i) => (
              <PriceCell
                key={i}
                cell={c}
                border="black"
                discountColor={ACCENT_RED}
                colIndex={i + 2}
              />
            ))}
          </Row>
        </PriceTable>
      </HairTables>
      <div className={classes.blackContainer}>
        <div aria-hidden="true">
          {SPARKLES.map((s, i) => (
            <Sparkle
              key={i}
              $x={s.x}
              $y={s.y}
              $size={s.size}
              $color={s.color}
              $dur={s.dur}
              $delay={s.delay}
            >
              {s.char}
            </Sparkle>
          ))}
        </div>
        <SectionTitle
          className={classes.titleContainer2}
          left={
            <>
              <OutlinedTxt
                className={classes.titleInlineLeft2}
                shadow={GREY_SHADOW}
              >
                デザイン
              </OutlinedTxt>
              <OutlinedTxt
                className={classes.titleInlineRight2}
                shadow={GREY_SHADOW}
              >
                カラー
              </OutlinedTxt>
            </>
          }
          badge={
            <FloatingTxt color="#ffff1a" shadow={ORANGE_SHADOW}>
              MENU
            </FloatingTxt>
          }
        />
        <DarkTables data-reveal>
          <PriceTable
            role="table"
            aria-label="デザインカラー料金表"
            aria-colcount={5}
            $labelCol="minmax(240px, 2.2fr)"
            $cols={4}
          >
            <HeadRow cols={COLS4} border="white" />
            {DESIGN_ROWS.map(row => (
              <Row role="row" key={row.pre + row.name}>
                <RowLabel role="rowheader" aria-colindex={1} $align="right">
                  <RowLabelTxt>
                    <span style={{ color: "white" }}>{row.pre}</span>
                    <span style={{ color: row.color }}>{row.name}</span>
                  </RowLabelTxt>
                </RowLabel>
                {row.cells.map((c, i) => (
                  <PriceCell
                    key={i}
                    cell={c}
                    border="white"
                    discountColor="red"
                    colIndex={i + 2}
                  />
                ))}
              </Row>
            ))}
          </PriceTable>
        </DarkTables>
        <SectionTitle
          className={classes.titleContainer2}
          left={
            <OutlinedTxt
              className={classes.titleInlineLeft3}
              shadow={GREY_SHADOW}
            >
              エクストラ
            </OutlinedTxt>
          }
          badge={
            <FloatingTxt color="slateblue" shadow={ORANGE_SHADOW}>
              OPTION
            </FloatingTxt>
          }
        />
        <DarkTables data-reveal>
          <PriceTable
            role="table"
            aria-label="エクストラOPTION料金表"
            aria-colcount={5}
            $labelCol="minmax(260px, 2.2fr)"
            $cols={4}
          >
            <HeadRow cols={COLS4} border="white" />
            {EXTRA_ROWS.map(row => (
              <Row role="row" key={row.parts.map(p => p.t).join("")}>
                <RowLabel role="rowheader" aria-colindex={1} $align="right">
                  <RowLabelTxt>
                    {row.parts.map(p => (
                      <span key={p.t} style={{ color: p.c }}>
                        {p.t}
                      </span>
                    ))}
                  </RowLabelTxt>
                </RowLabel>
                {row.cells.map((price, i) => (
                  <OptionCell key={i} role="cell" aria-colindex={i + 2}>
                    {price}
                    <span className="visually-hidden">円</span>
                  </OptionCell>
                ))}
              </Row>
            ))}
            <Row role="row">
              <RowLabel role="rowheader" aria-colindex={1} $align="right">
                <RowLabelTxt>
                  <span style={{ color: "white" }}>☆ポイント</span>
                  <span style={{ color: "yellow" }}>メッシュ</span>
                </RowLabelTxt>
              </RowLabel>
              <FoilCell role="cell" aria-colindex={2} aria-colspan={4}>
                ホイル1枚　968
                <span className="visually-hidden">円</span>
              </FoilCell>
            </Row>
          </PriceTable>
          <ExtraNote>
            ※デザインにより使用枚数が異なります。ホイル枚数及びデザインは
            <u>ご相談の上</u>で決めて参ります。
          </ExtraNote>
        </DarkTables>
        <div className={classes.starSection} style={{ marginTop: "2.8rem" }}>
          <div className={classes.pointBox} data-reveal>
            <Typography
              variant="h4"
              component="h2"
              className={classes.pointTitle}
            >
              ★前日割で<span style={{ color: ACCENT_RED }}>20</span>%オフ★
            </Typography>
            <Typography variant="body1" className={classes.pointDesc}>
              <span style={{ color: ACCENT_RED, textDecoration: "underline" }}>
                赤字
              </span>
              の価格は、<span style={{ color: "blue" }}>前日割</span>(
              <u>
                前日<span style={{ color: ACCENT_RED }}>19</span>時まで
              </u>
              のご予約)にてとってもお得！ぜひご利用ください。
            </Typography>
            <hr />
            <Typography
              variant="h4"
              component="h2"
              className={classes.pointTitle}
            >
              ★組み合わせて全てを実現★
            </Typography>
            <Typography variant="body1" className={classes.pointDesc}>
              <u>
                ヘア<span style={{ color: "deeppink" }}>カラー</span>MENU
              </u>
              (おしゃれ染め等)、
              <u>
                <span style={{ color: ACCENT_RED }}>デザイン</span>
                <span style={{ color: "blue" }}>カラー</span>MENU
              </u>
              (インナーカラー等)、 更に
              <u>
                <span style={{ color: "darkorange" }}>エクストラ</span>
                <span style={{ color: "purple" }}>OPTION</span>
              </u>
              を組合せ理想に限りなく近づけます。
            </Typography>
            <hr />
            <Typography
              variant="h4"
              component="h2"
              className={classes.pointTitle}
            >
              ★ギャラリーと連動★
            </Typography>
            <Typography variant="body1" className={classes.pointDesc}>
              インスタの<u>魔術カルテ</u>をご覧下さい♪ 記載の
              <span style={{ color: ACCENT_RED }}>PRICE</span>
              は、この[MENU/価格表]と
              <span style={{ color: ACCENT_RED }}>完全連動</span>
              ！「何をどうして何がおいくらか」を分かり易く！
            </Typography>
            <hr />
            <Typography
              variant="h4"
              component="h2"
              className={classes.pointTitle}
            >
              ★更に<span style={{ color: ACCENT_RED }}>おトク</span>に★
            </Typography>
            <Typography variant="body1" className={classes.pointDesc}>
              <span style={{ color: "blue" }}>[セルフ]</span>
              をご希望→[ご自身ブロー]で更に
              <span style={{ color: ACCENT_RED }}>お安く</span>♪
              スタイリング剤も豊富にご用意！
            </Typography>
          </div>
        </div>
        <div style={{ padding: "2rem", backgroundColor: "white" }}>
          <Typography
            variant="body2"
            align="right"
            className={classes.hosokuTxt}
          >
            <u>※価格はシャンプー料金込みとなっております。</u>
          </Typography>
          <Typography
            variant="body2"
            align="right"
            className={classes.hosokuTxt}
          >
            <u>
              ※ブロー料金につきましては、髪の長さ[S/M 550]、[L/LL
              1,100]となります。
            </u>
          </Typography>
        </div>
      </div>
    </Layout>
  )
}

export default MenuPage
